// Errors that are normal when the connection drops. SleekHear is built to keep
// working offline, so these must not surface as crashes or error overlays.

/**
 * True for a failed network request ("Failed to fetch", "NetworkError…",
 * "Load failed"), a Supabase retryable fetch error, or Supabase's auth-lock
 * handover ("Lock broken by another request with the 'steal' option") that
 * happens while a token refresh is stuck retrying offline.
 */
export function isExpectedNetworkError(reason) {
  if (!reason) return false;
  const message = String(reason.message || reason);
  const name = reason.name || '';
  if (name === 'AuthRetryableFetchError') return true;
  if (name === 'AbortError') return /lock broken|steal/i.test(message);
  return /failed to fetch|networkerror|network request failed|load failed/i.test(message);
}

/**
 * Installs a window-level handler that swallows only those expected errors
 * (logging a warning instead). Anything else still reaches the normal error
 * reporting. Registered in the capture phase so it runs before the dev overlay.
 */
export function installNetworkErrorGuard(target = window) {
  const handler = (event) => {
    if (isExpectedNetworkError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // eslint-disable-next-line no-console
      console.warn('Ignored an expected network error:', (event.reason && event.reason.message) || event.reason);
    }
  };
  target.addEventListener('unhandledrejection', handler, true);
  return () => target.removeEventListener('unhandledrejection', handler, true);
}

/**
 * Resolves with the promise's result, or with { timedOut: true } if it takes
 * longer than `ms`; a rejection resolves with { error }. Never rejects, so a
 * slow network call cannot hang the caller, and the original promise is still
 * handled if it settles late.
 */
export function withTimeout(promise, ms) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ timedOut: true }), ms);
    Promise.resolve(promise).then(
      (value) => { clearTimeout(timer); resolve({ value }); },
      (error) => { clearTimeout(timer); resolve({ error }); }
    );
  });
}
